import { useTranslation } from 'react-i18next';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

interface Row {
  labelKey: string;
  patronez: string;
  sansan: string;
  apollo: string;
  clay: string;
  zoominfo: string;
}

const ROWS: Row[] = [
  {
    labelKey: 'compare.row_pricing',
    patronez: 'Yes — full price page',
    sansan: 'No — sales call',
    apollo: 'Partial — $59 floor',
    clay: 'Partial — credit-based',
    zoominfo: 'No — sales call',
  },
  {
    labelKey: 'compare.row_free',
    patronez: '5 seats forever',
    sansan: 'None',
    apollo: 'Limited (50 credits)',
    clay: 'Limited',
    zoominfo: 'None',
  },
  {
    labelKey: 'compare.row_onboarding',
    patronez: '< 15 minutes',
    sansan: 'Weeks (with scanner shipment)',
    apollo: 'Hours',
    clay: 'Hours (steep learning curve)',
    zoominfo: 'Weeks',
  },
  {
    labelKey: 'compare.row_ocr',
    patronez: 'Phone-snap, 99% on JP cards',
    sansan: 'Best in class (with scanner)',
    apollo: 'No',
    clay: 'No',
    zoominfo: 'No',
  },
  {
    labelKey: 'compare.row_registry',
    patronez: 'Native + TDB premium',
    sansan: 'Native (their moat)',
    apollo: 'Thin partner data',
    clay: 'Via provider stack',
    zoominfo: 'Thin partner data',
  },
  {
    labelKey: 'compare.row_signals',
    patronez: 'Native (job, funding, hiring, TDNet, tech-install)',
    sansan: 'No',
    apollo: 'Yes (global only)',
    clay: 'Yes (you assemble it)',
    zoominfo: 'Yes (global only)',
  },
  {
    labelKey: 'compare.row_keigo',
    patronez: '4 registers, JP-native',
    sansan: 'No',
    apollo: 'EN→JA translate (uncanny)',
    clay: 'EN→JA translate (uncanny)',
    zoominfo: 'EN→JA translate (uncanny)',
  },
  {
    // AI configuration row. PatronEZ ships AI pre-configured and the
    // customer never sees the underlying model. Competitors either
    // have no AI or lock you to one.
    labelKey: 'compare.row_ai_config',
    patronez: 'Included · no setup',
    sansan: 'No AI',
    apollo: 'Requires setup',
    clay: 'Requires per-step setup',
    zoominfo: 'Requires setup',
  },
  {
    labelKey: 'compare.row_pipeline',
    patronez: 'Yes',
    sansan: 'No',
    apollo: 'Yes',
    clay: 'No (not a CRM)',
    zoominfo: 'Yes (basic)',
  },
  {
    labelKey: 'compare.row_hanko',
    patronez: 'First-class',
    sansan: 'Limited',
    apollo: 'No',
    clay: 'No',
    zoominfo: 'No',
  },
  {
    labelKey: 'compare.row_audit',
    patronez: 'Enterprise tier',
    sansan: 'Enterprise',
    apollo: 'Enterprise',
    clay: 'Enterprise',
    zoominfo: 'Enterprise',
  },
];

export function ComparePage() {
  const { t } = useTranslation();
  useDocumentMeta({
    title: 'Compare — PatronEZ vs Sansan, Apollo, Clay, ZoomInfo',
    description: 'How PatronEZ stacks up against Sansan, Apollo, Clay, and ZoomInfo on JP 法人番号 enrichment, global signals, keigo composition, and public pricing.',
    path: '/compare',
  });
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-6 pt-20 pb-24">
        <h1 className="display text-4xl md:text-5xl font-semibold tracking-tight text-ink-900 serif-jp">
          {t('compare.title')}
        </h1>
        <p className="mt-3 text-ink-600 max-w-2xl">{t('compare.subtitle')}</p>

        <div className="mt-10 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-ink-500 border-b border-ink-200">
                <th className="py-3 pr-4 font-medium"> </th>
                <th className="py-3 px-3 font-semibold text-accent-600">PatronEZ</th>
                <th className="py-3 px-3 font-medium">Sansan</th>
                <th className="py-3 px-3 font-medium">Apollo.io</th>
                <th className="py-3 px-3 font-medium">Clay</th>
                <th className="py-3 px-3 font-medium">ZoomInfo</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.labelKey} className="border-b border-ink-100">
                  <td className="py-3 pr-4 font-medium text-ink-900 align-top">{t(r.labelKey)}</td>
                  <td className="py-3 px-3 text-accent-700 font-medium align-top">{r.patronez}</td>
                  <td className="py-3 px-3 text-ink-600 align-top">{r.sansan}</td>
                  <td className="py-3 px-3 text-ink-600 align-top">{r.apollo}</td>
                  <td className="py-3 px-3 text-ink-600 align-top">{r.clay}</td>
                  <td className="py-3 px-3 text-ink-600 align-top">{r.zoominfo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-8 text-xs text-ink-500 max-w-3xl">{t('compare.fineprint')}</p>
      </main>
      <SiteFooter />
    </div>
  );
}
